import { useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  CalendarCheck,
  Eye,
  ShoppingBag,
  Siren,
  Home,
  Camera,
  FileCheck,
  MessageCircle,
  ArrowRight,
  HeartHandshake,
} from "lucide-react";
import { useContent } from "@/context/ContentContext";
import { waLink, resolveImage, renderLinkedText } from "@/lib/content";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { WhatsAppFloat } from "@/components/WhatsAppFloat";
import { PageSeo } from "@/components/PageSeo";

const WORRY_ICONS = [CalendarCheck, Eye, ShoppingBag, Siren];
const AUDIENCE_ICONS = [Home, CalendarCheck, Camera];

const reveal = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] },
};

export default function ParentCare() {
  const { content } = useContent();
  const p = content.parent_care_page;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="grain bg-[#FAF8F5]" data-testid="parent-care-page">
      <PageSeo path="/nri-parent-care" faqs={p.faqs} />
      <Navbar />
      <main>
        <section className="relative overflow-hidden bg-[#1C1917] pb-24 pt-40 lg:pb-32 lg:pt-48" data-testid="pc-hero">
          <div className="absolute inset-0 opacity-25">
            <img src={resolveImage(content.services.care.image)} alt="" className="hero-image-grade h-full w-full object-cover" />
          </div>
          <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-10">
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="mb-8 inline-flex items-center gap-3 rounded-full border border-[#FAF8F5]/25 bg-black/20 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.25em] text-[#FAF8F5]/90 backdrop-blur-md"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-[#C5A059]" />
              {p.kicker}
            </motion.p>
            <h1 className="font-display text-4xl font-medium leading-[1.1] tracking-tight text-[#FAF8F5] sm:text-5xl lg:text-6xl" data-testid="pc-headline">
              {p.headline_line1}{" "}
              <span className="italic text-[#C5A059]">{p.headline_line2}</span>
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.2 }}
              className="mt-8 max-w-2xl text-base leading-relaxed text-[#FAF8F5]/85 md:text-lg"
            >
              {renderLinkedText(p.subtext)}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.35 }}
              className="mt-10 flex flex-wrap gap-4"
            >
              <a
                href={waLink(content.business.whatsapp_number, p.whatsapp_msg)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="pc-whatsapp-cta"
                className="inline-flex items-center gap-2 rounded-full bg-[#25D366] px-8 py-4 text-sm font-bold uppercase tracking-wider text-[#1C1917] transition-[background-color,transform] duration-300 hover:bg-[#1fb857] hover:scale-[1.03]"
              >
                <MessageCircle size={16} />
                Free Family Consultation
              </a>
              <Link
                to="/#packages"
                data-testid="pc-packages-link"
                className="inline-flex items-center gap-2 rounded-full border border-[#FAF8F5]/40 px-8 py-4 text-sm font-bold uppercase tracking-wider text-[#FAF8F5] transition-colors duration-300 hover:bg-[#FAF8F5]/10"
              >
                See Care Packages <ArrowRight size={15} />
              </Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 0.5 }}
              className="mt-14 flex flex-wrap gap-x-12 gap-y-4 border-t border-[#FAF8F5]/15 pt-8"
            >
              {p.stats.map((s) => (
                <div key={s} className="flex items-center gap-3">
                  <span className="h-px w-8 bg-[#C5A059]" />
                  <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#FAF8F5]/75">{s}</span>
                </div>
              ))}
            </motion.div>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-6 py-24 lg:px-10" data-testid="pc-worries">
          <motion.div {...reveal} className="mb-14 max-w-2xl">
            <p className="mb-6 text-[11px] font-bold uppercase tracking-[0.3em] text-[#7A2B21]">{p.worries_kicker}</p>
            <h2 className="font-display text-4xl font-medium leading-[1.1] tracking-tight text-[#1C1917] sm:text-5xl">
              {p.worries_title1}{" "}
              <span className="italic text-[#7A2B21]">{p.worries_title2}</span>
            </h2>
          </motion.div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {p.worries.map((w, i) => {
              const Icon = WORRY_ICONS[i] || Eye;
              return (
                <motion.div
                  key={w.title}
                  {...reveal}
                  transition={{ ...reveal.transition, delay: i * 0.08 }}
                  className="rounded-3xl border border-[#1C1917]/10 bg-white/60 p-7"
                  data-testid={`pc-worry-${i}`}
                >
                  <Icon size={22} className="mb-4 text-[#7A2B21]" />
                  <h3 className="font-display text-lg font-medium text-[#1C1917]">{renderLinkedText(w.title)}</h3>
                  <p className="mt-2 text-xs leading-relaxed text-[#57534E]">{renderLinkedText(w.body)}</p>
                </motion.div>
              );
            })}
          </div>
        </section>

        <section className="mx-auto max-w-5xl px-6 pb-24 lg:px-10" data-testid="pc-image-interlude">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="arch-frame overflow-hidden shadow-[0_40px_80px_rgba(28,25,23,0.18)]"
          >
            <img
              src={resolveImage(p.image)}
              alt={p.image_alt || "NRI parent care — elderly parents cared for at home in the Tricity"}
              data-testid="pc-interlude-image"
              className="hero-image-grade h-[380px] w-full object-cover md:h-[480px]"
            />
          </motion.div>
          <motion.p
            {...reveal}
            className="mx-auto mt-8 max-w-xl text-center font-display text-2xl italic leading-relaxed text-[#7A2B21] md:text-3xl"
            data-testid="pc-interlude-quote"
          >
            "{p.image_quote}"
          </motion.p>
        </section>

        <section className="bg-[#FAF8F5] pb-24" data-testid="pc-audience">
          <div className="mx-auto max-w-7xl px-6 lg:px-10">
            <motion.div {...reveal} className="mb-14 max-w-2xl">
              <p className="mb-6 text-[11px] font-bold uppercase tracking-[0.3em] text-[#7A2B21]">{p.audience.kicker}</p>
              <h2 className="font-display text-4xl font-medium leading-[1.1] tracking-tight text-[#1C1917] sm:text-5xl">
                {p.audience.title1}{" "}
                <span className="italic text-[#7A2B21]">{p.audience.title2}</span>
              </h2>
              <p className="mt-6 text-base leading-relaxed text-[#57534E]">{renderLinkedText(p.audience.intro)}</p>
            </motion.div>
            <div className="grid gap-6 md:grid-cols-3">
              {p.audience.items.map((item, i) => {
                const Icon = AUDIENCE_ICONS[i] || Home;
                return (
                  <motion.div
                    key={item.title}
                    {...reveal}
                    transition={{ ...reveal.transition, delay: i * 0.08 }}
                    className="rounded-3xl border border-[#1C1917]/10 bg-white p-8 shadow-[0_20px_40px_rgba(28,25,23,0.06)]"
                    data-testid={`pc-audience-${i}`}
                  >
                    <div className="mb-5 inline-flex h-12 w-12 items-center justify-center rounded-full bg-[#2C4C3B]/10 text-[#2C4C3B]">
                      <Icon size={20} />
                    </div>
                    <h3 className="font-display text-xl font-medium text-[#1C1917]">{renderLinkedText(item.title)}</h3>
                    <p className="mt-3 text-sm leading-relaxed text-[#57534E]">{renderLinkedText(item.body)}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        <section className="bg-[#2C4C3B] py-24" data-testid="pc-services">
          <div className="mx-auto grid max-w-7xl gap-14 px-6 lg:grid-cols-12 lg:px-10">
            <motion.div {...reveal} className="lg:col-span-5">
              <p className="mb-6 text-[11px] font-bold uppercase tracking-[0.3em] text-[#C5A059]">{p.services_kicker}</p>
              <h2 className="font-display text-4xl font-medium leading-[1.1] tracking-tight text-[#FAF8F5] sm:text-5xl">
                {p.services_title1}{" "}
                <span className="italic text-[#C5A059]">{p.services_title2}</span>
              </h2>
              <p className="mt-6 text-sm leading-relaxed text-[#FAF8F5]/70">
                {renderLinkedText(p.services_intro)}
              </p>
              <p className="mt-8 rounded-2xl border border-[#FAF8F5]/20 bg-[#1C1917]/20 p-5 text-xs leading-relaxed text-[#FAF8F5]/80">
                <span className="font-bold uppercase tracking-wider text-[#C5A059]">Honest note:</span>{" "}
                {renderLinkedText(p.honest_note)}
              </p>
            </motion.div>
            <div className="lg:col-span-7">
              <ul className="space-y-4">
                {p.services.map((s, i) => (
                  <motion.li
                    key={s}
                    {...reveal}
                    transition={{ ...reveal.transition, delay: i * 0.06 }}
                    className="flex items-start gap-4 rounded-2xl border border-[#FAF8F5]/15 bg-[#FAF8F5]/5 px-6 py-5"
                    data-testid={`pc-service-${i}`}
                  >
                    <FileCheck size={18} className="mt-0.5 shrink-0 text-[#C5A059]" />
                    <span className="text-sm leading-relaxed text-[#FAF8F5]/90">{renderLinkedText(s)}</span>
                  </motion.li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-6 py-24 lg:px-10" data-testid="pc-steps">
          <motion.div {...reveal} className="mb-14 max-w-2xl">
            <p className="mb-6 text-[11px] font-bold uppercase tracking-[0.3em] text-[#7A2B21]">{p.steps_kicker}</p>
            <h2 className="font-display text-4xl font-medium leading-[1.1] tracking-tight text-[#1C1917] sm:text-5xl">
              {p.steps_title1}{" "}
              <span className="italic text-[#7A2B21]">{p.steps_title2}</span>
            </h2>
          </motion.div>
          <div className="grid gap-10 lg:grid-cols-3">
            {p.steps.map((s, i) => (
              <motion.div
                key={s.num}
                {...reveal}
                transition={{ ...reveal.transition, delay: i * 0.1 }}
                className="border-t-2 border-[#C5A059] pt-8"
                data-testid={`pc-step-${s.num}`}
              >
                <span className="font-display text-5xl font-light text-[#C5A059]">{s.num}</span>
                <h3 className="mt-4 font-display text-xl font-medium text-[#1C1917]">{renderLinkedText(s.title)}</h3>
                <p className="mt-2 text-sm leading-relaxed text-[#57534E]">{renderLinkedText(s.body)}</p>
              </motion.div>
            ))}
          </div>
        </section>

        <section className="bg-[#FAF8F5] pb-24" data-testid="pc-faq">
          <div className="mx-auto max-w-4xl px-6">
            <motion.div {...reveal} className="mb-12">
              <p className="mb-6 text-[11px] font-bold uppercase tracking-[0.3em] text-[#7A2B21]">{p.faq_kicker}</p>
              <h2 className="font-display text-3xl font-medium tracking-tight text-[#1C1917] sm:text-4xl">
                {p.faq_title1} <span className="italic text-[#7A2B21]">{p.faq_title2}</span>
              </h2>
            </motion.div>
            <div className="space-y-8">
              {p.faqs.map((f, i) => (
                <motion.div key={f.q} {...reveal} transition={{ ...reveal.transition, delay: i * 0.08 }}
                  className="border-l-2 border-[#C5A059] pl-8" data-testid={`pc-faq-${i}`}>
                  <h3 className="font-display text-lg font-medium text-[#1C1917]">{renderLinkedText(f.q)}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-[#57534E]">{renderLinkedText(f.a)}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-[#7A2B21] py-24" data-testid="pc-cta">
          <div className="mx-auto flex max-w-5xl flex-col items-start gap-10 px-6 lg:flex-row lg:items-center lg:justify-between lg:px-10">
            <motion.div {...reveal}>
              <h2 className="font-display text-3xl font-medium leading-tight tracking-tight text-[#FAF8F5] sm:text-4xl">
                {p.cta_title1}{" "}
                <span className="italic text-[#C5A059]">{p.cta_title2}</span>
              </h2>
              <p className="mt-4 max-w-lg text-sm leading-relaxed text-[#FAF8F5]/80">
                {renderLinkedText(p.cta_subtext)}
              </p>
            </motion.div>
            <motion.a
              {...reveal}
              href={waLink(content.business.whatsapp_number, p.cta_whatsapp_msg)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="pc-bottom-whatsapp"
              className="inline-flex shrink-0 items-center gap-3 rounded-full bg-[#FAF8F5] px-8 py-4 text-sm font-bold uppercase tracking-wider text-[#7A2B21] transition-[background-color,transform] duration-300 hover:bg-[#C5A059] hover:text-[#1C1917] hover:scale-[1.03]"
            >
              <HeartHandshake size={17} />
              {p.cta_button}
            </motion.a>
          </div>
        </section>
      </main>
      <Footer />
      <WhatsAppFloat />
    </div>
  );
}
